"use client";

import { CustomerConfirmSignUpStepForm } from "@/components/auth/customer-confirm-signup-step-form";
import { CustomerLoginCodeStepForm } from "@/components/auth/customer-login-code-step-form";
import { LinkButton } from "@/components/ui/link";
import { AddressStepForm } from "@/components/onboarding/address-step-form";
import { SignupStepForm } from "@/components/onboarding/signup-step-form";
import { ScreenTransition } from "@/components/shared/screen-transition";
import { ONBOARDING_ENTRY, type OnboardingIntent } from "@/lib/onboarding/onboarding-intent";
import { onboardingProgress } from "@/lib/onboarding/onboarding-progress";
import { ONBOARDING_VOICE } from "@/lib/onboarding/onboarding-voice";
import { StepLine } from "./step-line";
import { useSignupWizard, type SignupStep } from "./use-signup-wizard";

interface SignupFlowProps {
  cpf: string;
  /** Which door this is — decides the voice, the destination and whether a list is provisioned. */
  intent: OnboardingIntent;
  redirectTo: string;
  initialStep?: SignupStep;
  skipConfirmation?: boolean;
  /** Shows "Não é você?" CTA (user has existing session). */
  showIdentityEscape?: boolean;
  onBack: () => void;
}

export function SignupFlow({ cpf, intent, redirectTo, initialStep, skipConfirmation, showIdentityEscape, onBack }: SignupFlowProps) {
  const { step, maskedEmail, errors, signupData, addressData, isPending, actions } = useSignupWizard({
    cpf,
    intent,
    redirectTo,
    initialStep,
    skipConfirmation,
  });

  function renderStep() {
    switch (step) {
      case "signup":
        return (
          <SignupStepForm
            cpf={cpf}
            lede={ONBOARDING_VOICE[intent].signupLede}
            isPending={false}
            errorMessage={errors.signup}
            emailError={errors.signupEmail}
            initialValues={signupData}
            onBack={onBack}
            onSubmit={(values) => actions.submitSignup(values)}
            onEmailChange={() => actions.clearEmailError?.()}
          />
        );

      case "address":
        return (
          <AddressStepForm
            isPending={isPending.register || isPending.saveAddress}
            errorMessage={errors.address}
            initialValues={addressData}
            onBack={skipConfirmation ? undefined : () => actions.goBack("signup")}
            onSubmit={(values) => actions.submitAddress(values)}
          />
        );

      case "confirmRegister":
        return (
          <CustomerConfirmSignUpStepForm
            email={signupData?.email ?? ""}
            isPending={isPending.confirmRegister}
            errorMessage={errors.confirmRegister}
            onBack={() => actions.goBack("address")}
            onSubmit={(values) => actions.confirmRegister(values)}
            onResend={() => actions.resendRegister()}
            isResending={isPending.resendRegister}
          />
        );

      case "confirmLogin":
        return (
          <CustomerLoginCodeStepForm
            email={maskedEmail || (signupData?.email ?? "")}
            isPending={isPending.confirmLogin}
            errorMessage={errors.confirmLogin}
            onSubmit={(values) => actions.confirmLogin(values)}
            onResend={() => actions.resendLogin()}
            isResending={isPending.resendLogin}
          />
        );

      /*
       * No terms step. The code above is the last thing this wizard asks: the terms belong to
       * the list, and the list asks them itself, after the event (§5.6) — `CreateListTerms` on
       * `/anfitriao/nova-lista/termos`. A guest never had them, having no list.
       */
    }
  }

  // The code steps keep the CPF step's narrower card (`auth-card`, 440px); the forms take 560px.
  const isCodeStep = step === "confirmRegister" || step === "confirmLogin";

  return (
    <div className={isCodeStep ? "w-full max-w-[440px]" : "max-w-[560px]"}>
      {showIdentityEscape && (
        <LinkButton
          variant="muted"
          className="mb-4"
          onClick={async () => {
            await fetch("/api/auth/logout", { method: "POST" }).catch(() => {});
            // Back to the same door, not always the host one: whoever came to pay for a gift
            // is still here to pay for a gift, under another CPF.
            window.location.href = ONBOARDING_ENTRY[intent];
          }}
        >
          Não é você? Usar outro CPF
        </LinkButton>
      )}
      <StepLine {...onboardingProgress({ step, intent, skipConfirmation })} />
      <ScreenTransition
        screenKey={step}
        scrollTarget={step === "signup" && errors.signupEmail ? "#email" : "top"}
        animateExit
        skipFirstEntrance
      >
        {renderStep()}
      </ScreenTransition>
    </div>
  );
}
